/**
 * What an entry knows about where it was written, beyond the page itself
 * (F-013, D-054; dev.md, *Additional information on an entry*, 2026-10-14).
 *
 * Two parts, from two sources:
 *
 * - **app** - what the reviewed application says about its logged-in account:
 *   the roles, the account's display name, and whatever else it can name. The
 *   application sets three attributes on the SDK's own script tag -
 *   `data-loupe-role`, `data-loupe-user`, `data-loupe-context` (a JSON object
 *   of short texts) - and the SDK reads them when an entry is captured, not at
 *   load, because a person logs in, out and between accounts while the page
 *   stays. Shown as *laut Anwendung* (AC-014): it is the application's
 *   statement, not Loupe's finding.
 * - **client** - the browser, its system and language, which the SDK reads
 *   from the browser itself. A tester need not type it.
 *
 * Neither is verified (dev.md, *What Loupe trusts, and what it checks*):
 * everything the SDK sends is the reviewer's testimony, and reproduction is the
 * check. What IS enforced is size - the server keeps known fields and caps
 * every text, so a page cannot turn an entry into a dump.
 */

export interface AppContext {
  /** The account's roles as the application names them - free text. */
  role?: string;
  /** The account's display name - never a mail address, by the application's contract. */
  user?: string;
  /** Whatever else the application can name: label -> value. */
  context?: Record<string, string>;
}

export interface ClientInfo {
  /** Name and major version, e.g. `Firefox 131`. */
  browser?: string;
  /** The operating system family, e.g. `Windows`, `macOS`, `Android`. */
  os?: string;
  /** The browser's language, e.g. `de-DE`. */
  language?: string;
}

/** The caps the server holds a page to. */
export const APP_LIMITS = { text: 200, key: 40, entries: 12, client: 80 } as const;

const clip = (value: unknown, max: number): string | undefined => {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text.length === 0 ? undefined : text.slice(0, max);
};

/**
 * The application's statement, bounded: strings only, every text capped, at
 * most `entries` context lines, and nothing at all when nothing usable is left.
 * Used by the SDK on what it reads and by the server on what it receives - the
 * same rule on both sides, so a page that writes too much sees it cut where it
 * is written.
 */
export function boundAppContext(value: unknown): AppContext | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const out: AppContext = {};
  const role = clip(raw.role, APP_LIMITS.text);
  const user = clip(raw.user, APP_LIMITS.text);
  if (role) out.role = role;
  if (user) out.user = user;
  if (raw.context && typeof raw.context === "object" && !Array.isArray(raw.context)) {
    const lines: Record<string, string> = {};
    for (const [key, entry] of Object.entries(raw.context as Record<string, unknown>)) {
      if (Object.keys(lines).length >= APP_LIMITS.entries) break;
      const label = clip(key, APP_LIMITS.key);
      const text = clip(typeof entry === "number" || typeof entry === "boolean" ? String(entry) : entry, APP_LIMITS.text);
      if (label && text) lines[label] = text;
    }
    if (Object.keys(lines).length > 0) out.context = lines;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function boundClient(value: unknown): ClientInfo | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const out: ClientInfo = {};
  const browser = clip(raw.browser, APP_LIMITS.client);
  const os = clip(raw.os, APP_LIMITS.client);
  const language = clip(raw.language, APP_LIMITS.client);
  if (browser) out.browser = browser;
  if (os) out.os = os;
  if (language) out.language = language;
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Read the three attributes off the SDK's script tag. Unreadable JSON in
 * `data-loupe-context` is dropped whole rather than stored half - a context
 * that might be wrong is worse than none.
 */
export function readAppContext(dataset: { loupeRole?: string; loupeUser?: string; loupeContext?: string }): AppContext | undefined {
  let context: unknown;
  if (dataset.loupeContext) {
    try {
      context = JSON.parse(dataset.loupeContext);
    } catch {
      context = undefined;
    }
  }
  return boundAppContext({ role: dataset.loupeRole, user: dataset.loupeUser, context });
}

/** Browser names as a person says them, in the order a user agent must be tested. */
const BROWSERS: [RegExp, string][] = [
  [/Edg(?:e|A|iOS)?\/(\d+)/, "Edge"],
  [/OPR\/(\d+)/, "Opera"],
  [/SamsungBrowser\/(\d+)/, "Samsung Internet"],
  [/Firefox\/(\d+)/, "Firefox"],
  [/FxiOS\/(\d+)/, "Firefox"],
  [/CriOS\/(\d+)/, "Chrome"],
  [/Chrome\/(\d+)/, "Chrome"],
  [/Version\/(\d+)[\d.]* .*Safari\//, "Safari"],
];

const SYSTEMS: [RegExp, string][] = [
  [/Windows NT/, "Windows"],
  [/Android/, "Android"],
  [/iPhone|iPad|iPod/, "iOS"],
  [/CrOS/, "ChromeOS"],
  [/Mac OS X|Macintosh/, "macOS"],
  [/Linux/, "Linux"],
];

/**
 * The browser and system from a user agent, as name and major version. Which
 * Windows or macOS version a user agent claims is frozen by every current
 * browser, so the family is all that can honestly be said.
 */
export function describeClient(userAgent: string, language?: string): ClientInfo {
  const out: ClientInfo = {};
  for (const [pattern, name] of BROWSERS) {
    const match = pattern.exec(userAgent);
    if (match) {
      out.browser = `${name} ${match[1]}`;
      break;
    }
  }
  for (const [pattern, name] of SYSTEMS) {
    if (pattern.test(userAgent)) {
      out.os = name;
      break;
    }
  }
  if (language) out.language = language;
  return boundClient(out) ?? {};
}

/** One line for a list or a report: `Melder + Manager · TS K7`. */
export function appLine(app: AppContext | undefined): string {
  if (!app) return "";
  return [app.role, app.user].filter(Boolean).join(" · ");
}

/** One line for the browser: `Firefox 131 · Windows · de-DE`. */
export function clientLine(client: ClientInfo | undefined): string {
  if (!client) return "";
  return [client.browser, client.os, client.language].filter(Boolean).join(" · ");
}

/** The text a search runs over - every value the application named, and the browser. */
export function contextSearchText(view: unknown): string {
  const v = (view ?? {}) as { app?: AppContext; client?: ClientInfo };
  const app = v.app ?? {};
  return [app.role, app.user, ...Object.values(app.context ?? {}), v.client?.browser, v.client?.os]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}
